import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil as EditIcon, Play as ExecuteIcon, History as HistoryIcon } from 'lucide-react';
import { Button, Chip, ShellHeader } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { CodeEditor } from '../components/common/CodeEditor';
import { agentFunctionService } from '../services/agentFunctionService';
import { ROUTES } from '../utils/routes';
import type { AgentFunctionDetailDto, AgentFunctionRequestDto } from '../types/agentFunction';

const getStatusChip = (status: number) => {
  const map: Record<number, { label: string; color: 'default' | 'amber' | 'green' | 'red' }> = {
    0: { label: 'Pending', color: 'default' },
    1: { label: 'Processing', color: 'amber' },
    2: { label: 'Completed', color: 'green' },
    3: { label: 'Failed', color: 'red' },
  };
  const cfg = map[status] ?? { label: 'Unknown', color: 'default' };
  return <Chip color={cfg.color} label={cfg.label} />;
};

/**
 * Agent Function detail page showing definition, schemas, prompt, and recent executions.
 */
export const AgentFunctionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [func, setFunc] = useState<AgentFunctionDetailDto | null>(null);
  const [recentRequests, setRecentRequests] = useState<AgentFunctionRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [detail, requests] = await Promise.all([
          agentFunctionService.getAgentFunction(id),
          agentFunctionService.getAgentFunctionRequests(id, { pageNumber: 1, pageSize: 5 }),
        ]);
        setFunc(detail);
        setRecentRequests(requests.items);
      } catch (err: any) {
        const msg = err.response?.data?.message || 'Failed to load agent function';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  if (loading) {
    return (
      <div className="agent-function-detail" data-testid="agent-function-detail-page">
        <ShellHeader title="Agent Function" description="Loading…" />
        <PageContainer>
          <LoadingSkeleton rows={6} data-testid="agent-function-detail-loading" />
        </PageContainer>
      </div>
    );
  }

  if (error || !func) {
    return (
      <div className="agent-function-detail" data-testid="agent-function-detail-page">
        <ShellHeader
          title="Agent Function"
          description="Function not found."
          actions={
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(ROUTES.AGENT_FUNCTIONS)} data-testid="back-agent-function-btn">
              Back to Agent Functions
            </Button>
          }
        />
        <PageContainer>
          <InlineErrorPanel message={error || 'Agent function not found'} data-testid="agent-function-detail-error" />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="agent-function-detail" data-testid="agent-function-detail-page">
      <ShellHeader
        title={func.name}
        description={`Slug: ${func.slug}`}
        actions={
          <div className="flex items-center gap-1">
            <Chip
              color={func.isActive ? 'green' : 'default'}
              variant={func.isActive ? 'filled' : 'outlined'}
              label={func.isActive ? 'Active' : 'Inactive'}
              data-testid="agent-function-status"
            />
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(ROUTES.AGENT_FUNCTIONS)} data-testid="back-agent-function-btn">
              Back
            </Button>
          </div>
        }
      />

      <PageContainer>
      {/* Action Buttons */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <Button
          type="button"
          variant="default"
          size="sm"
          className="gap-2"
          onClick={() => navigate(`${ROUTES.AGENT_FUNCTION_EXECUTE}?slug=${encodeURIComponent(func.slug)}`)}
          data-testid="execute-agent-function-btn"
        >
          <ExecuteIcon className="h-4 w-4" />
          Execute Function
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => navigate(ROUTES.AGENT_FUNCTION_EDIT.replace(':id', func.id))}
          data-testid="edit-agent-function-btn"
        >
          <EditIcon className="h-4 w-4" />
          Edit
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => navigate(ROUTES.AGENT_FUNCTION_REQUESTS.replace(':id', func.id))}
          data-testid="agent-function-link-history"
        >
          <HistoryIcon className="h-4 w-4" />
          View Execution History ({func.requestCount})
        </Button>
      </div>

      {/* Overview */}
      <FormSection title="Overview" data-testid="agent-function-section-overview">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-[var(--color-text-secondary)]">
                Agent Definition
              </span>
              <p>{func.agentDefinitionName}</p>
            </div>
            <div>
              <span className="text-xs text-[var(--color-text-secondary)]">
                Version
              </span>
              <p>{func.version}</p>
            </div>
            <div>
              <span className="text-xs text-[var(--color-text-secondary)]">
                Created At
              </span>
              <p>{new Date(func.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <span className="text-xs text-[var(--color-text-secondary)]">
                Updated At
              </span>
              <p>{new Date(func.updatedAt).toLocaleString()}</p>
            </div>
            {func.description && (
              <div className="sm:col-span-2">
                <span className="text-xs text-[var(--color-text-secondary)]">
                  Description
                </span>
                <p>{func.description}</p>
              </div>
            )}
          </div>
      </FormSection>

      {/* System Prompt */}
      <FormSection title="System Prompt" data-testid="agent-function-section-prompt">
          <CodeEditor
            value={func.systemPrompt}
            language="plaintext"
            height="250px"
            readOnly
          />
      </FormSection>

      {/* Schemas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <FormSection title="Input Schema" data-testid="agent-function-section-input-schema">
            <CodeEditor
              value={func.inputSchemaJson}
              language="json"
              height="200px"
              readOnly
            />
        </FormSection>
        <FormSection title="Output Schema" data-testid="agent-function-section-output-schema">
            <CodeEditor
              value={func.outputSchemaJson}
              language="json"
              height="200px"
              readOnly
            />
        </FormSection>
      </div>

      {/* Recent Executions */}
      <FormSection title="Recent Executions" data-testid="agent-function-section-executions">
          <div className="flex items-center justify-end mb-2">
            {func.requestCount > 5 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(ROUTES.AGENT_FUNCTION_REQUESTS.replace(':id', func.id))}
              >
                View All ({func.requestCount})
              </Button>
            )}
          </div>
          {recentRequests.length === 0 ? (
            <p className="text-sm text-[var(--color-text-secondary)] py-2 text-center">
              No executions yet. Click &quot;Execute Function&quot; to run this agent function.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {recentRequests.map((req) => (
                <div
                  key={req.id}
                  className="flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-[var(--color-bg-inset)]"
                  onClick={() =>
                    navigate(ROUTES.AGENT_FUNCTION_REQUEST_DETAIL.replace(':requestId', req.id))
                  }
                >
                  {getStatusChip(req.status)}
                  <span className="text-sm flex-1 truncate">
                    {req.inputJson.length > 80
                      ? `${req.inputJson.substring(0, 80)}...`
                      : req.inputJson}
                  </span>
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    {req.durationMs != null ? `${req.durationMs}ms` : '—'}
                  </span>
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    {new Date(req.createdAt).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
      </FormSection>
      </PageContainer>
    </div>
  );
};
